import * as THREE from "three";
import type { Avatar } from "./avatar";
import { compileObjectAsync } from "../engine/compileObjectAsync";

/** Reversible, finite transition: closing halfway through never restarts the pose. */
export class CompanionTransition {
  open = false;
  progress = 0;
  get active(): boolean { return this.open || this.progress > 0; }
  get weight(): number { return THREE.MathUtils.smootherstep(this.progress, 0, 1); }
  update(dt: number, reducedMotion = false): void {
    if (!Number.isFinite(dt) || dt <= 0) return;
    this.progress = THREE.MathUtils.clamp(this.progress + (this.open ? 1 : -1) * Math.min(dt, .1) / (reducedMotion ? .2 : .85), 0, 1);
  }
}

/** A real world-space companion, attached to the animated palm, with no external assets. */
export class Companion {
  readonly transition = new CompanionTransition();
  readonly group = new THREE.Group();
  readonly gaze = new THREE.Vector3();
  readonly pointer = new THREE.Vector2();
  private readonly orb = new THREE.Group();
  private readonly face = new THREE.Group();
  private readonly eyes: THREE.Mesh[] = [];
  private readonly light = new THREE.PointLight(0x71ccff, 0, 2, 2);
  private readonly targetCamera = new THREE.PerspectiveCamera();
  private readonly eye = new THREE.Vector3();
  private readonly target = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private readonly q = new THREE.Quaternion();
  private readonly arcStart = new THREE.Spherical();
  private readonly arcEnd = new THREE.Spherical();
  private readonly arcPoint = new THREE.Vector3();
  private readonly ring: THREE.Mesh;
  private readonly glow: THREE.ShaderMaterial;
  private readonly shell: THREE.ShaderMaterial;
  private time = 0;
  private response = 0;
  reducedMotion = false;

  constructor(scene: THREE.Scene) {
    this.group.name = "holographic-companion";
    this.group.visible = false;
    // Light counts are part of Three's shader cache key. Keep the fill in the
    // scene at zero intensity while closed: hiding it with the orb would rebuild
    // every lit world material on the first Tab press (and switch them on exit).
    this.light.name = "companion-fill";
    scene.add(this.group, this.light);
    this.group.add(this.orb);
    const sphere = new THREE.SphereGeometry(.115, 40, 28);
    this.shell = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, toneMapped: false,
      uniforms: { time: { value: 0 }, opacity: { value: 0 } },
      vertexShader: `varying vec3 n; varying vec3 v; varying vec3 p;
        void main(){ p=position; vec4 mv=modelViewMatrix*vec4(position,1.); n=normalize(normalMatrix*normal); v=normalize(-mv.xyz); gl_Position=projectionMatrix*mv; }`,
      fragmentShader: `varying vec3 n; varying vec3 v; varying vec3 p; uniform float time; uniform float opacity;
        void main(){ float rim=pow(1.-abs(dot(normalize(n),normalize(v))),2.4);
          float scan=.5+.5*sin(p.y*420.-time*2.); vec3 col=mix(vec3(.015,.06,.16),vec3(.24,.68,1.),rim);
          gl_FragColor=vec4(col+vec3(.02,.065,.09)*scan,(.58+rim*.42)*opacity); }`,
    });
    this.orb.add(new THREE.Mesh(sphere, this.shell));
    this.glow = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, toneMapped: false,
      uniforms: { opacity: { value: 0 } },
      vertexShader: `varying vec2 p; void main(){p=uv*2.-1.;gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.);}`,
      fragmentShader: `varying vec2 p; uniform float opacity; void main(){float a=pow(max(0.,1.-length(p)),3.);gl_FragColor=vec4(.18,.48,1.,a*.7*opacity);}`,
    });
    const halo = new THREE.Mesh(new THREE.PlaneGeometry(.65, .65), this.glow);
    this.face.add(halo);
    const eyeMat = new THREE.MeshBasicMaterial({ color: 0xb7f2ff, toneMapped: false });
    for (const x of [-.036, .036]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(1, 20, 14), eyeMat);
      eye.position.set(x, .006, .11); eye.scale.set(.015, .025, .007);
      this.eyes.push(eye); this.face.add(eye);
    }
    this.orb.add(this.face);
    this.ring = new THREE.Mesh(new THREE.TorusGeometry(.11, .0015, 6, 64), new THREE.MeshBasicMaterial({ color: 0x64caff, transparent: true, opacity: .6, toneMapped: false, depthWrite: false }));
    this.ring.rotation.x = Math.PI / 2;
    this.group.add(this.ring);
  }

  react(): void { this.response = 1; }

  /** Compile only the small orb ahead of interaction, without drawing a frame. */
  async warmup(renderer: THREE.WebGLRenderer, camera: THREE.Camera): Promise<void> {
    await compileObjectAsync(renderer, this.group, camera, this.group.parent as THREE.Scene);
  }

  update(dt: number): void {
    this.transition.update(dt, this.reducedMotion);
    this.time += Math.min(Math.max(dt, 0), .1);
    this.response *= Math.exp(-4 * dt);
    const w = this.transition.weight;
    this.group.visible = w > .001;
    this.group.scale.setScalar(Math.max(.001, THREE.MathUtils.smoothstep(w, .15, .9)));
    this.shell.uniforms.time.value = this.reducedMotion ? 0 : this.time;
    this.shell.uniforms.opacity.value = w;
    this.glow.uniforms.opacity.value = w;
    this.light.intensity = w * .7;
    const bob = this.reducedMotion ? 0 : Math.sin(this.time * 1.7) * .012 + this.response * .025;
    this.orb.position.y = .27 + bob;
    this.ring.position.y = .025;
    this.ring.scale.setScalar(1 + this.response * .22);
    const blinkPhase = this.time % 4.7;
    const blink = this.reducedMotion ? 1 : 1 - Math.max(0, 1 - Math.abs(blinkPhase - 4.35) / .095) * .94;
    for (const eye of this.eyes) eye.scale.y = .025 * blink * (1 + this.response * .2);
  }

  /** Call before the avatar samples its pose; gaze follows the same local hand target. */
  prepareAvatar(avatar: Avatar, available: boolean): void {
    const weight = available ? this.transition.weight : 0;
    avatar.setCompanionPose(weight, this.reducedMotion ? 0 : this.time, this.response);
    if (weight > 0) {
      avatar.group.updateMatrixWorld(true);
      this.gaze.set(-.38, 1.48, -.48).applyMatrix4(avatar.group.matrixWorld);
      avatar.setHeadLookTarget(this.gaze, true);
    }
  }

  /** Blend over this frame's live gameplay camera; never save stale world coordinates. */
  frame(camera: THREE.PerspectiveCamera, avatar: Avatar | null): void {
    const w = this.transition.weight;
    if (w <= 0) return;
    if (avatar) {
      avatar.group.updateMatrixWorld(true);
      const hand = avatar.humanoidBone("hand_l");
      if (hand) {
        // The wrist is behind the middle of the palm in the posed hand's +X frame.
        this.group.position.set(.075, -.005, 0).applyMatrix4(hand.matrixWorld);
      } else this.group.position.set(-.38, 1.17, -.48).applyMatrix4(avatar.group.matrixWorld);
      avatar.group.getWorldQuaternion(this.q);
      this.group.quaternion.copy(this.q);
      this.up.set(0, 1, 0).applyQuaternion(this.q);
      const narrow = camera.aspect < 1;
      this.eye.set(2.0, 1.8, -2.8).multiplyScalar(narrow ? 1.2 : 1).applyMatrix4(avatar.group.matrixWorld);
      // Look past the pair to reserve the right half of the image for the menu.
      this.target.set(narrow ? -1 : -.72, narrow ? .9 : 1.27, -.12).applyMatrix4(avatar.group.matrixWorld);
      this.targetCamera.position.copy(this.eye);
      this.targetCamera.up.copy(this.up);
      this.targetCamera.lookAt(this.target);
      if (!this.reducedMotion) {
        // Travel around the player instead of cutting through their torso on entry.
        avatar.group.worldToLocal(this.arcPoint.copy(camera.position));
        this.arcPoint.y -= 1.27;
        this.arcStart.setFromVector3(this.arcPoint);
        avatar.group.worldToLocal(this.arcPoint.copy(this.eye));
        this.arcPoint.y -= 1.27;
        this.arcEnd.setFromVector3(this.arcPoint);
        const angle = Math.atan2(Math.sin(this.arcEnd.theta - this.arcStart.theta), Math.cos(this.arcEnd.theta - this.arcStart.theta));
        this.arcStart.theta += angle * w;
        this.arcStart.phi = THREE.MathUtils.lerp(this.arcStart.phi, this.arcEnd.phi, w);
        this.arcStart.radius = THREE.MathUtils.lerp(this.arcStart.radius, this.arcEnd.radius, w);
        this.arcPoint.setFromSpherical(this.arcStart); this.arcPoint.y += 1.27;
        camera.position.copy(this.arcPoint.applyMatrix4(avatar.group.matrixWorld));
        camera.quaternion.slerp(this.targetCamera.quaternion, w);
        camera.up.lerp(this.up, w).normalize();
        camera.fov = THREE.MathUtils.lerp(camera.fov, 43, w);
        camera.updateProjectionMatrix();
      }
    } else {
      // Cockpit/vehicle menus keep the driving camera and project the companion nearby.
      this.group.position.set(-.65, -.05, -2.1).applyQuaternion(camera.quaternion).add(camera.position);
      this.group.quaternion.copy(camera.quaternion);
    }
    this.light.position.copy(this.group.position);
    this.group.updateMatrixWorld(true);
    this.orb.getWorldQuaternion(this.q).invert();
    this.face.quaternion.copy(this.q).multiply(camera.quaternion);
    this.face.rotation.y += this.pointer.x * .1;
    this.face.rotation.x -= this.pointer.y * .08;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.light.removeFromParent();
    const geometries = new Set<THREE.BufferGeometry>(), materials = new Set<THREE.Material>();
    this.group.traverse(o => { if (o instanceof THREE.Mesh) { geometries.add(o.geometry); for (const m of Array.isArray(o.material) ? o.material : [o.material]) materials.add(m); } });
    geometries.forEach(g => g.dispose()); materials.forEach(m => m.dispose()); this.light.dispose();
  }
}
